import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import {
  MdAdd,
  MdGroups,
  MdEventAvailable,
  MdCheckCircle,
  MdChevronRight,
  MdPersonAddAlt1,
} from 'react-icons/md';
import toast from 'react-hot-toast';
import { AuthContext } from 'contexts/AuthContext';
import pdSessionService from 'ApiService/PdSession';
import api from 'ApiConfig';
import AssignSessionModal from 'pages/Common/AssignSessionModal';

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  completed:
    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const StatCard = ({ icon, label, value, tone }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-4 dark:border-white/10">
    <span
      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl ${tone}`}
    >
      {icon}
    </span>
    <div>
      <p className="text-2xl font-bold text-navy-700 dark:text-white">
        {value}
      </p>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-300">
        {label}
      </p>
    </div>
  </div>
);

const StatusBadge = ({ status }) => (
  <span
    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold capitalize ${
      STATUS_STYLES[status] ||
      'bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300'
    }`}
  >
    {status}
  </span>
);

const PdSessionList = () => {
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const hrRole = state?.user?.hrRole;
  const layout = `/${
    state?.user?.role === 'placement'
      ? ['headhr', 'jrhr'].includes(hrRole)
        ? hrRole
        : 'placement'
      : state?.user?.role
  }`;

  // Only admins can assign teachers / HR to a session.
  const isAdmin = ['admin', 'superadmin'].includes(state?.user?.role);

  const [sessions, setSessions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [mineOnly, setMineOnly] = useState(false);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchFilter, fromDate, toDate, mineOnly]);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/combinedroutes/get-all-branches');
      if (response.data.success) {
        setBranches(response.data.branches || response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (branchFilter) params.branch = branchFilter;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (mineOnly) params.mine = 'true';

      const response = await pdSessionService.getSessions(params);
      if (response.data.success) {
        setSessions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching PD sessions:', error);
      toast.error('Failed to fetch PD sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '-';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    if (start.toDateString() === end.toDateString()) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} – ${formatDate(endDate)}`;
  };

  const stats = useMemo(() => {
    const scheduled = sessions.filter((s) => s.status === 'scheduled').length;
    const completed = sessions.filter((s) => s.status === 'completed').length;
    return { total: sessions.length, scheduled, completed };
  }, [sessions]);

  const hasActiveFilters = branchFilter || fromDate || toDate || mineOnly;

  return (
    <Card extra="w-full pb-10 p-4">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h2 className="flex items-center gap-2 text-xl font-bold text-navy-700 dark:text-white">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-100 text-brand-500 dark:bg-navy-700">
            <MdGroups className="h-5 w-5" />
          </span>
          PD Sessions
        </h2>
        <button
          onClick={() => navigate(`${layout}/pd-session/create`)}
          className="flex items-center gap-1 rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-brand-600"
        >
          <MdAdd className="h-5 w-5" />
          Create PD Session
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<MdGroups />}
          label="Total Sessions"
          value={stats.total}
          tone="bg-brand-100 text-brand-500 dark:bg-navy-700 dark:text-brand-400"
        />
        <StatCard
          icon={<MdEventAvailable />}
          label="Scheduled"
          value={stats.scheduled}
          tone="bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300"
        />
        <StatCard
          icon={<MdCheckCircle />}
          label="Completed"
          value={stats.completed}
          tone="bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300"
        />
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 p-4 dark:border-white/10">
        <div className="min-w-[180px]">
          <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
            Branch
          </label>
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white"
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch._id} value={branch._id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        <InputField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          extra="min-w-[160px]"
        />
        <InputField
          label="To Date"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          extra="min-w-[160px]"
        />

        <label className="mb-3 flex items-center gap-2 text-sm font-medium text-navy-700 dark:text-white">
          <input
            type="checkbox"
            checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)}
          />
          My sessions only
        </label>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setBranchFilter('');
              setFromDate('');
              setToDate('');
              setMineOnly(false);
            }}
            className="mb-3 text-sm font-medium text-brand-500 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-bold uppercase tracking-wide text-gray-500 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300">
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Created by</th>
              <th className="px-4 py-3">Assigned to</th>
              <th className="px-4 py-3">Branches</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Students</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-sm text-gray-500">
                  Loading sessions...
                </td>
              </tr>
            ) : sessions.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-14">
                  <div className="flex flex-col items-center gap-2 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-500 dark:bg-navy-700">
                      <MdGroups className="h-6 w-6" />
                    </span>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-300">
                      {hasActiveFilters
                        ? 'No PD sessions match these filters.'
                        : 'No PD sessions yet.'}
                    </p>
                    <button
                      onClick={() => navigate(`${layout}/pd-session/create`)}
                      className="mt-1 text-sm font-medium text-brand-500 hover:underline"
                    >
                      Create your first PD session
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              sessions.map((session) => (
                <tr
                  key={session._id}
                  onClick={() =>
                    navigate(`${layout}/pd-session/detail?id=${session._id}`)
                  }
                  className="group cursor-pointer border-b border-gray-100 text-sm transition hover:bg-brand-50/60 dark:border-white/5 dark:text-white dark:hover:bg-white/5"
                >
                  <td className="px-4 py-3 font-semibold text-navy-700 dark:text-white">
                    {session.title}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {session.createdBy === state?.user?.userId
                      ? 'You'
                      : session.createdByName}{' '}
                    <span className="text-xs text-gray-400">
                      ({session.createdByLabel})
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {(session.assignedTo || []).length
                      ? session.assignedTo.map((a) => a.name).join(', ')
                      : '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {(session.branches || [])
                      .map((branch) => branch.name)
                      .join(', ') || '-'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {formatDateRange(session.startDate, session.endDate)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:bg-navy-700 dark:text-gray-200">
                      <MdGroups className="h-3.5 w-3.5" />
                      {session.students?.length || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={session.status} />
                  </td>
                  <td className="px-4 py-3 text-right text-gray-400">
                    <div className="flex items-center justify-end gap-2">
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssigning(session);
                          }}
                          className="flex items-center gap-1 rounded-lg border border-brand-200 px-2.5 py-1 text-xs font-medium text-brand-500 hover:bg-brand-50 dark:border-white/10 dark:hover:bg-white/5"
                        >
                          <MdPersonAddAlt1 className="h-4 w-4" />
                          Assign
                        </button>
                      )}
                      <MdChevronRight className="h-5 w-5 transition group-hover:translate-x-0.5 group-hover:text-brand-500" />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AssignSessionModal
        isOpen={Boolean(assigning)}
        session={assigning}
        service={pdSessionService}
        onClose={() => setAssigning(null)}
        onSaved={(updated) =>
          setSessions((prev) =>
            prev.map((s) =>
              s._id === updated._id
                ? { ...s, assignedTo: updated.assignedTo }
                : s
            )
          )
        }
      />
    </Card>
  );
};

export default PdSessionList;
